import { Dialog } from '@angular/cdk/dialog';
import { Injector, Provider, StaticProvider, Type } from '@angular/core';
import { Observable } from 'rxjs';
import { ApModalTokens } from './ApModalComponentType';
import { AnyModalComponent } from './AnyModalComponent';
import { ApDialogConfig, ApDialogRef } from './ApDialogConfig';
import { ExactlyPartial } from 'webcommon/shared/types-ng1520';
import { Overlay, OverlayContainer } from '@angular/cdk/overlay';

// This is a type useful for situations where you need to reference the generic type of a class via type inferencing
// when there are multiple generic types declared on a function or class.
// Ideally, the function below would have the type parameters like this:
// <TComponent extends ApBaseModalComponent<TResult>, TResult>
// but then TResult will not get inferred correctly, meaning you would have to tell the function
// what type it needs to be in the <> when calling it.
// see https://stackoverflow.com/q/58847047/5335355 for an example
export type InferModalResult<T> = T extends AnyModalComponent<infer TResult> ? TResult : never;
export type InferModalData<T> = T extends AnyModalComponent ? T['modalData'] : never;

export abstract class ApModalServiceBase {
  // shortcut to provide a subclass of ApModalServiceBase as ApModalServiceBase
  static provideAsBase(): Provider {
    return {
      provide: ApModalServiceBase,
      useExisting: this,
    };
  }

  abstract showComponentModal<
    TComponent extends AnyModalComponent,
    TComponentState extends ExactlyPartial<TComponent, TComponentState>,
  >(
    component: Type<TComponent>,
    config?: ApDialogConfig<TComponent, InferModalData<TComponent>, TComponentState>,
  ): Promise<InferModalResult<TComponent>>;

  abstract showComponentModal$<
    TComponent extends AnyModalComponent,
    TComponentState extends ExactlyPartial<TComponent, TComponentState>,
  >(
    component: Type<TComponent>,
    config?: ApDialogConfig<TComponent, InferModalData<TComponent>, TComponentState>,
  ): Observable<InferModalResult<TComponent>>;

  // use this to more easily fill in providers property in ModalOptions with stateful services
  // todo: should I just use the Injector from the component calling this as the injector on the DialogConfig,
  //  and then I shouldn't have to manually provide these non-singleton classes.
  //  The only downside would be if there were ones that I explicitly didn't want to be available for the modals
  provideClasses(elementInjector: Injector, ...tokens: ApModalTokens): StaticProvider[] {
    return tokens.map((token) => {
      return {
        provide: token,
        useFactory: () => {
          return elementInjector.get(token);
        },
      };
    });
  }

  protected makeCompatibleWithBootstrapModalAllClosed(_this: {
    documentElement: HTMLElement;
    overlayContainer: OverlayContainer;
  }): void {
    // after all the dialogs are closed, remove this class from the document
    _this.documentElement.classList.remove('ap-cdk-bootstrap-modal-override');
    _this.overlayContainer.getContainerElement().classList.remove('ap-cdk-bootstrap-modal-override');
  }

  protected makeCompatibleWithBootstrapModal<TComponent = unknown, TData = unknown, TInitialState = unknown>(
    pureMutable_config: ApDialogConfig<TComponent, TData, TInitialState>,
    _this: {
      overlay: Overlay;
    },
  ): void {
    // mimic the default behavior of ngx-bootstrap config for backwards compatibility,
    // so that all the current modals don't have to be refactored to work/look correctly.
    // TODO: We should probably take this out eventually to go more off of material standards
    // by using this: https://v15.material.angular.io/components/dialog/overview
    pureMutable_config.positionStrategy =
      pureMutable_config.positionStrategy ?? _this.overlay.position().global().centerHorizontally().top();
    // I'm not sure what special behavior some modals may have, so turning off the autofocus for now
    pureMutable_config.autoFocus = undefined;
  }

  protected makeCompatibleWithBootstrapModalAfterOpen<TComponent = unknown>(
    dialogRef: ApDialogRef<TComponent>,
    _this: {
      dialog: Dialog;
      documentElement: HTMLElement;
      overlayContainer: OverlayContainer;
    },
  ): void {
    // https://github.com/valor-software/ngx-bootstrap/blob/v9.0.0/src/component-loader/component-loader.class.ts#L410-L413
    // We should be using 'data' instead of 'initialState' going forward.
    // We have to do this after the ctor, so it doesn't get overwritten by default values in subclass ctor.
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    Object.assign(dialogRef.componentInstance!, dialogRef.config.initialState);
    // If there are issues with assigning it here, then there are some options for where to do this:
    // alternative 1: assign initialState after dialog.open() call in ApModalService with the returned
    // DialogRef.componentInstance (this current code)
    // alternative 2: assign initialState in subclass override CdkDialogContainer.attachComponentPortal()
    // alternative 3: assign initialState in ApModalHelper.makeCompatibleWithBootstrapModalOnInit()
    // (some components are expecting these values to already be filled in before ngOnInit)
    // https://v15.material.angular.io/cdk/dialog/overview#providing-a-custom-dialog-container

    // if no dialogs were previously open (this is the first one opened), add this class to the document
    if (_this.dialog.openDialogs.length === 1) {
      _this.documentElement.classList.add('ap-cdk-bootstrap-modal-override');
      _this.overlayContainer.getContainerElement().classList.add('ap-cdk-bootstrap-modal-override');
    }
  }
}
